import { useCallback } from "react";
import "./DropzoneFile.scss";
import { useDropzone } from "react-dropzone";
import { useThemeContext } from "@/context/ThemeContext";
import { FormikProps } from "formik";
import { notifyAlert } from "@/utils/NotificationFunctions";
import { UploadType } from "../UploadImages/UploadImages";

export interface handleData {
  file: File;
  image: any;
}
interface DataI {
  name: string;
  year: string;
  month: string;
  day: string;
  description: string;
  collectionId: string;
  era: string;
  dateEnd: string;
  dateStart: string;
  order: string;
  copyright?: string;
  credit?: string;
  number?: string;
}
interface DropzoneFilePropsI {
  selectedFiles: Array<any>;
  setSelectedFiles: React.Dispatch<React.SetStateAction<Array<any>>>;
  uploadType?: UploadType;
  formik: FormikProps<any>;
  fieldName: string;
  handleData?: (props: handleData[]) => void;
}
const FileUploadIcon = "../../assets/images/icons/file-upload-icon.svg";
function DropzoneFile(props: DropzoneFilePropsI) {
  const { theme } = useThemeContext();
  const isValidDate = (year: string, month: string, day: string): boolean => {
    if (year.length !== 4 || month.length !== 2 || day.length !== 2) {
      return false;
    }
    if (isNaN(Number(year)) || isNaN(Number(month)) || isNaN(Number(day))) {
      return false;
    }
    if (Number(month) < 1 || Number(month) > 12) {
      return false;
    }
    if (Number(day) < 1 || Number(day) > 31) {
      return false;
    }
    return true;
  };
  const orderValidation = (order: string): boolean => {
    if (isNaN(Number(order))) {
      return false;
    }
    return true;
  };
  function processFiles(imageTemp: Array<any>, uploadType?: UploadType) {
    let upload_error_message = "";
    const imagesTemp: string[] = [];
    const imageData: { file: File; data: DataI | {} }[] = [];
    const imageDataFolded: handleData[] = [];

    const processImage = (file: File) => {
      return new Promise<void>((resolve, reject) => {
        const removeCopyRight = file.name.split("&");
        const hasCopyright = removeCopyRight.length > 1;
        const fileNameParts = removeCopyRight[0].split("_");
        const dateParts = fileNameParts[1]?.split("-");
        const fileOrder = fileNameParts[2]?.split(".")[0];
        const cright = hasCopyright ? removeCopyRight[1]?.split(".")[0] : null;
        const credit = hasCopyright ? removeCopyRight[2]?.split(".")[0] : null;
        const number = hasCopyright ? removeCopyRight[3]?.split(".")[0] : null;
        const data = {
          name: fileNameParts[0],
          year: dateParts[0]?.split(".")[0],
          month: dateParts[1]?.split(".")[0],
          day: dateParts[2]?.split(".")[0],
          description: "",
          collectionId: "",
          era: "",
          dateEnd: "",
          dateStart: "",
          order: fileOrder || "0",
          ...(cright && { copyright: cright }),
          ...(credit && { credit: credit }),
          ...(number && { number: number }),
        };
        console.log(data);
        imageData.push({ file: file, data });
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (_event) => {
          imagesTemp.push(reader.result as string);
          imageDataFolded.push({ file: file, image: reader.result });
          resolve();
        };
        reader.onerror = reject;
        if (!orderValidation(data.order)) {
          throw new Error("order");
        }
        if (!isValidDate(data.year, data.month, data.day)) {
          throw new Error("date");
        }
        if (hasCopyright && (!cright || !credit || !number)) {
          throw new Error("copyright");
        }
      });
    };

    if (!uploadType || uploadType === "single") {
      return new Promise<void>((resolve, reject) => {
        const file = imageTemp[0];
        imageData.push({ file: file, data: {} });

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (_event) => {
          imagesTemp.push(reader.result as string);
          imageDataFolded.push({ file: file, image: reader.result });
          resolve();
        };
        reader.onerror = reject;
      })
        .then(() => {
          props.formik.setFieldValue("files", imageData);
          props.setSelectedFiles(imagesTemp);
          if (props.handleData) {
            props.handleData(imageDataFolded);
          }
        })
        .catch((err) => {
          upload_error_message = "Error during file processing";
          notifyAlert(upload_error_message);
        });
    } else {
      const promises = imageTemp.map((file) => processImage(file));
      Promise.all(promises)
        .then(() => {
          props.formik.setFieldValue("files", imageData);
          props.setSelectedFiles(imagesTemp);
          if (props.handleData) {
            props.handleData(imageDataFolded);
          }
        })
        .catch((err) => {
          console.log(err);
          if (err.message === "order") {
            upload_error_message =
              "Invalid file name format. Order must be a number.\n Expected format:\n name_yyyy-mm-dd_order.extension";
          } else if (err.message === "date") {
            upload_error_message = `Invalid file name format. Year, month or day should be valid numbers.\n Expected format:\n name_yyyy-mm-dd.extension or name_yyyy-mm-dd_order.extension`;
          } else if (err.message === "copyright") {
            upload_error_message =
              "Invalid file name format. Copyright, credit or number missing.\n Expected format:\n name_yyyy-mm-dd&copyright&credit&number.extension or name_yyyy-mm-dd_order&copyright&credit&number.extension";
          } else {
            upload_error_message =
              "Invalid file name format. \nExpected format:\n name_yyyy-mm-dd.extension or name_yyyy-mm-dd_order.extension";
          }

          notifyAlert(upload_error_message);
        });
    }
  }

  const onDrop = useCallback(
    (acceptedFiles: any) => {
      processFiles(acceptedFiles, props.uploadType);
    },
    [props.uploadType]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  return (
    <div
      {...getRootProps()}
      className={`dropzone-component ${isDragActive ? "active" : ""} ${theme}`}
    >
      <input id="drop-file" {...getInputProps()} />
      <img src={FileUploadIcon} />
      <p>PNG, GIF, WEBP or JPEG. Max 512Mb.</p>
    </div>
  );
}

export default DropzoneFile;
